import { Location } from '@angular/common';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { StrategyItem, isCustomStrategy, customStrategyToStrategyItem } from '../../strategy-catalog.models';
import { StrategyCatalogService } from '../../services/strategy-catalog.service';
import { StrategyOptimizationService } from '../../services/strategy-optimization.service';
import { take, forkJoin, of, catchError, switchMap, map } from 'rxjs';
import { StrategyColumns } from '@pdqdev/pdq-shared/dist/entities/StrategyCatalog';
import { getTags, ConnectedTest } from '../../strategy-catalog.models';
import { optimizeCategoryName } from '../../strategy-catalog.models';
import { ToastService } from '@app/storybook/services/toast/toast.service';

@Component({
  selector: 'strategy-details',
  standalone: false,
  templateUrl: './strategy-details.component.html',
  styleUrl: './strategy-details.component.scss',
})
export class StrategyDetailsComponent implements OnInit {
  strategyId: string;
  strategyLoading = true;
  strategyItem: StrategyItem | null = null;
  connectedTest: ConnectedTest | null = null;
  isBookmarked = false;
  isCustom = false;
  deleting = false;
  strategyColumns = StrategyColumns;
  readonly getTags = getTags;
  readonly optimizeCategoryName = optimizeCategoryName;

  private destroyRef = inject(DestroyRef);

  constructor(
    private router: Router,
    private strategyCatalogService: StrategyCatalogService,
    private strategyOptimizationService: StrategyOptimizationService,
    private route: ActivatedRoute,
    private location: Location,
    private toastService: ToastService,
  ) {}
  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.strategyId = id;
      this.isCustom = isCustomStrategy(id);
      this.loadStrategy();
    } else {
      console.error('No strategy found in the route');
    }
  }
  loadStrategy(): void {
    const strategy$ = this.isCustom
      ? this.strategyCatalogService.getCustomStrategies().pipe(
          take(1),
          map((items) => {
            const found = items.find((cs) => cs.id === this.strategyId);
            return found ? customStrategyToStrategyItem(found) : undefined;
          }),
        )
      : this.strategyCatalogService.getStrategy(this.strategyId).pipe(take(1));

    forkJoin([
      strategy$,
      this.strategyOptimizationService.initialize().pipe(catchError(() => of(undefined))),
    ]).subscribe({
      next: ([strategy]) => {
        this.strategyItem = strategy ?? null;
        this.loadRoadmapState();
        this.strategyLoading = false;
      },
      error: () => {
        this.strategyLoading = false;
      },
    });
  }

  onBookmarkChanged(saved: boolean): void {
    this.isBookmarked = saved;
    this.connectedTest = this.strategyOptimizationService.getConnectedTest(this.strategyId);
  }

  deleteCustomStrategy(): void {
    this.deleting = true;
    this.strategyCatalogService
      .deleteCustomStrategy(this.strategyId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toastService.showToast({ message: 'Custom strategy deleted', mode: 'success' });
          this.router.navigate(['/optimize/strategy-catalog']);
        },
        error: () => {
          this.deleting = false;
          this.toastService.showToast({ message: 'Failed to delete strategy', mode: 'danger' });
        },
      });
  }

  navigateBack(): void {
    if (window.history.length > 1) {
      this.location.back();
    } else {
      this.router.navigate(['/optimize/strategy-catalog']);
    }
  }

  private loadRoadmapState(): void {
    this.isBookmarked = this.strategyOptimizationService.isStrategySaved(this.strategyId);
    this.connectedTest = this.strategyOptimizationService.getConnectedTest(this.strategyId);
  }
}
